import { fromJS } from 'immutable';
import { createSelector } from 'reselect';
import { GetBillingInfo, GetRecurringBilling, UnenrollAutopay } from '../../api/BillingInformation';
import { fetchUser } from './user';

// ACTIONS TYPES
export const BILLING_UPDATING = 'customer-portal/billing/BILLING_UPDATING';
export const FETCHING_BILLING = 'customer-portal/billing/FETCHING_BILLING';
export const FETCH_BILLING_FAILURE = 'customer-portal/billing/FETCH_BILLING_FAILURE';
export const FETCH_BILLING_SUCCESS = 'customer-portal/billing/FETCH_BILLING_SUCCESS';
export const FETCH_CONTRACT_MODS_FAILURE = 'customer-portal/billing/FETCH_CONTRACT_MODS_FAILURE';
export const FETCH_CONTRACT_MOD_SUCCESS = 'customer-portal/billing/FETCH_CONTRACT_MOD_SUCCESS';
export const FETCH_RECURRING_ACCOUNT_FAILURE = 'customer-portal/billing/FETCH_RECURRING_ACCOUNT_FAILURE';
export const FETCH_RECURRING_ACCOUNT_SUCCESS = 'customer-portal/billing/FETCH_RECURRING_ACCOUNT_SUCCESS';
export const GETTING_CONTRACT_MODS = 'customer-portal/billing/GETTING_CONTRACT_MODS';
export const GETTING_RECURRING_ACCOUNT = 'customer-portal/billing/GETTING_RECURRING_ACCOUNT';
export const SET_SHOULD_FETCH_BILLING = 'customer-portal/billing/SET_SHOULD_FETCH_BILLING';
export const SET_SELECTED_SPECIAL_INVOICE = 'customer-portal/billing/SET_SELECTED_SPECIAL_INVOICE';
export const UNENROLL_AUTOPAY_FAILURE = 'customer-portal/billing/UNENROLL_AUTOPAY_FAILURE';
export const UNENROLL_AUTOPAY_SUCCESS = 'customer-portal/billing/UNENROLL_AUTOPAY_SUCCESS';
export const SET_RECURRING_ACCOUNT_INFO = 'customer-portal/billing/SET_RECURRING_ACCOUNT_INFO';

// ACTION CREATORS
export const fetchBillingSuccess = data => ({
  type: FETCH_BILLING_SUCCESS,
  payload: data,
});

export const fetchBillingFailure = err => ({
  type: FETCH_BILLING_FAILURE,
  payload: err,
});

export const fetchRecurringAccountSuccess = data => ({
  type: FETCH_RECURRING_ACCOUNT_SUCCESS,
  payload: data,
});

export const fetchRecurringAccountFailure = (err, contractId) => ({
  type: FETCH_RECURRING_ACCOUNT_FAILURE,
  payload: {
    err,
    recurringAccount: {
      contractName: contractId,
    },
  },
});

export const fetchingBilling = () => ({
  type: FETCHING_BILLING,
});

export const gettingRecurringAccount = () => ({
  type: GETTING_RECURRING_ACCOUNT,
});

export const updatingBilling = () => ({
  type: BILLING_UPDATING,
});

export const setShouldFetchBilling = () => ({
  type: SET_SHOULD_FETCH_BILLING,
});

export const unEnrollAutopaySuccess = () => ({
  type: UNENROLL_AUTOPAY_SUCCESS,
});

export const unEnrollAutopayFailure = error => ({
  type: UNENROLL_AUTOPAY_FAILURE,
  payload: error,
});

export const setSelectedSpecialInvoice = specialInvoiceId => ({
  type: SET_SELECTED_SPECIAL_INVOICE,
  payload: specialInvoiceId,
});

export const setRecurringAccountInfo = ({ lastFour, payMethod }) => ({
  type: SET_RECURRING_ACCOUNT_INFO,
  payload: { lastFour, payMethod },
});

// THUNKS
export const fetchBilling = (prospectId, contactId) => async dispatch => {
  dispatch(fetchingBilling());
  try {
    const { data } = await GetBillingInfo(prospectId, contactId);
    return dispatch(fetchBillingSuccess(data));
  } catch (error) {
    // if billing endpoint returns a 400 with an invalid contact id,
    // call getUser to refresh the token
    if (error?.response?.data?.message === 'contactId is not valid') {
      dispatch(fetchUser());
      return dispatch(fetchBillingFailure('Please refresh or logout and login back in to view your billing information.'));
    }
    return dispatch(fetchBillingFailure('We are unable to retrieve your billing information at this time.'));
  }
};

export const shouldFetchBilling = billingState => !billingState.get('loaded') && !billingState.get('loading');

export const fetchBillingIfNeeded = (prospectId, contactId) => (dispatch, getState) => {
  if (!shouldFetchBilling(getState().billing)) {
    return null;
  }

  return dispatch(fetchBilling(prospectId, contactId));
};

export const unEnrollAutopay = prospectId => async dispatch => {
  dispatch(updatingBilling());
  try {
    await UnenrollAutopay(prospectId);
    dispatch(unEnrollAutopaySuccess());
    // Since it was successful we need to refetch the user info
    dispatch(fetchUser());
  } catch (error) {
    dispatch(unEnrollAutopayFailure(error));
  }
};

export const fetchRecurringAccount = contractId => async dispatch => {
  dispatch(gettingRecurringAccount());
  try {
    const { data } = await GetRecurringBilling(contractId);
    dispatch(fetchRecurringAccountSuccess(data));
  } catch (error) {
    dispatch(fetchRecurringAccountFailure(error, contractId));
  }
};

export const shouldFetchRecurringAccount = (billingState, contractId) => {
  const currentAccount = billingState.get('recurringAccount');
  return currentAccount?.contractName !== contractId;
};

export const fetchRecurringAccountIfNeeded = contractId => async (dispatch, getState) => {
  if (!shouldFetchRecurringAccount(getState().billing, contractId)) {
    return null;
  }

  return dispatch(fetchRecurringAccount(contractId));
};

// SELECTORS
const billingSelector = state => state.billing;
const userSelector = state => state.user;

export const getBillingInfo = createSelector([billingSelector], billing => ({
  lastDueDate: billing.get('lastDueDate'),
  lastModifiedDate: billing.get('lastModifiedDate'),
  totalAmountDue: parseFloat(billing.get('totalAmountDue')),
  lastInvoiceKey: billing.get('lastInvoiceKey'),
  lastInvoiceDate: billing.get('lastInvoiceDate'),
}));

export const getBillingHistory = createSelector([billingSelector], billing => billing.get('transactionHistory'));

export const getBillingLoading = createSelector([billingSelector], billing => billing.get('loading'));

export const getBillingUnenrollAutopaySuccess = createSelector([billingSelector], billing => billing.get('unenrollSuccess'));

export const getBillingUnenrollAutopayError = createSelector([billingSelector], billing => billing.get('unenrollError'));

export const getBillingHistoryError = createSelector([billingSelector], billing => billing.get('error'));

export const getBillingRecurringAccount = createSelector([billingSelector], billing => billing.get('recurringAccount'));

export const getSpecialInvoices = createSelector([billingSelector, userSelector], billing => {
  const specialPayments = billing.get('transactionHistory').filter(mod => mod.isSpecialInvoice && mod.status !== 'PAID');
  if (specialPayments?.length > 0) {
    return specialPayments.map(payment => ({
      amount: payment.amount,
      createdDate: payment.date,
      expiresDate: null,
      id: payment.id,
      type: payment.type,
      invoiceType: payment.invoiceType,
    }));
  }

  return null;
});

export const getSelectedSpecialInvoice = createSelector([billingSelector, userSelector], billing => {
  const selectedId = billing.get('specialInvoiceId');

  if (!selectedId) {
    return null;
  }

  const selectedSpecialInvoices = billing.get('transactionHistory').filter(payment => payment.id === selectedId);

  if (selectedSpecialInvoices?.length > 0) {
    return selectedSpecialInvoices[0];
  }

  return null;
});

// REDUCER
export const initialState = fromJS({
  specialInvoiceId: null,
  error: null,
  lastDueDate: '',
  lastInvoiceKey: '',
  lastInvoiceDate: '',
  lastModifiedDate: '',
  loaded: false,
  loading: false,
  recurringAccount: null,
  totalAmountDue: '',
  transactionHistory: [],
  unenrollError: false,
  unenrollSuccess: false,
});

export default (state = initialState, action) => {
  switch (action.type) {
    case FETCH_BILLING_SUCCESS:
      return state.set('loaded', true).set('loading', false).set('error', null).merge(action.payload);
    case UNENROLL_AUTOPAY_SUCCESS:
      return state.set('loading', false).set('unenrollError', false).set('unenrollSuccess', true);
    case FETCH_BILLING_FAILURE:
      return state.set('loading', false).set('error', action.payload);
    case UNENROLL_AUTOPAY_FAILURE:
      return state.set('loading', false).set('unenrollError', action.payload);
    case FETCHING_BILLING:
    case BILLING_UPDATING:
    case GETTING_RECURRING_ACCOUNT:
      return state.set('loading', true).set('error', null).set('unenrollError', false).set('unenrollSuccess', false);
    case SET_SHOULD_FETCH_BILLING:
      return state.set('loaded', false).set('loading', false);
    case FETCH_RECURRING_ACCOUNT_SUCCESS:
      return state.set('recurringAccount', action.payload).set('loading', false);
    case FETCH_RECURRING_ACCOUNT_FAILURE:
      return state.set('recurringAccount', action.payload.recurringAccount).set('loading', false);
    case SET_SELECTED_SPECIAL_INVOICE:
      return state.set('specialInvoiceId', action.payload);
    case SET_RECURRING_ACCOUNT_INFO: {
      const currentRecurringAccount = state.get('recurringAccount');
      if (!currentRecurringAccount) {
        return state;
      }
      // Update the recurring account info in the redux store to ensure that the latest info is displayed.
      return state.set('recurringAccount', {
        ...currentRecurringAccount,
        last_4: action.payload.lastFour,
        payMethod: action.payload.payMethod,
      });
    }
    default:
      return state;
  }
};
